import React from "react";
import { Edit, Share2, Trash2, Target } from "lucide-react";

/**
 * 🎯 MilestoneItem — HeadwaySpot
 * --------------------------------------------------------------
 * Displays a milestone row with title, summary, author, date,
 * and actions for edit / share / delete.
 * --------------------------------------------------------------
 * Props:
 * - milestone: {
 *     _id: string,
 *     title?: string,
 *     summary?: string,
 *     createdAt?: string | Date,
 *     dueDate?: string | Date,
 *     author?: { name?: string, email?: string }
 *   }
 * - onShare?: (milestone) => void
 * - onDelete?: (milestone) => void
 * - onEdit?: (milestone) => void
 * - sharing?: boolean
 * - deleting?: boolean
 */

export default function MilestoneItem({
  milestone = {},
  onShare,
  onDelete,
  onEdit,
  sharing = false,
  deleting = false,
}) {
  const title = milestone?.title || "Untitled Milestone";
  const summary = milestone?.summary || "";
  const author =
    milestone?.author?.name || milestone?.author?.email || "Unknown";

  const created = milestone?.createdAt ? new Date(milestone.createdAt) : null;
  const due = milestone?.dueDate ? new Date(milestone.dueDate) : null;

  return (
    <li className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all mb-2">
      <div className="flex items-start justify-between gap-4">
        {/* Left: Details */}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Target className="w-4 h-4 text-indigo-500" />
            <h3 className="font-medium text-gray-900 truncate">{title}</h3>
            <span className="text-xs text-gray-500">• {author}</span>
          </div>

          {summary && (
            <p className="mt-1 text-sm text-gray-600 line-clamp-2">{summary}</p>
          )}

          <div className="mt-1 text-xs text-gray-400 flex gap-3">
            {created && (
              <span>
                Created: {created.toLocaleDateString()}{" "}
                {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            {due && (
              <span>
                Due: <span className="text-gray-500">{due.toLocaleDateString()}</span>
              </span>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex shrink-0 items-center gap-2">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(milestone)}
              title="Edit milestone"
              className="inline-flex items-center gap-1.5 border border-gray-200 text-gray-700 text-sm px-3 py-1.5 rounded-lg hover:bg-gray-50"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
          )}

          {onShare && (
            <button
              type="button"
              onClick={() => onShare(milestone)}
              disabled={sharing}
              title="Share milestone to Slack"
              className="inline-flex items-center gap-1.5 border border-indigo-100 text-indigo-700 text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-50 disabled:opacity-50"
            >
              <Share2 className="w-4 h-4" />
              {sharing ? "Sharing..." : "Share"}
            </button>
          )}

          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(milestone)}
              disabled={deleting}
              title="Delete milestone"
              className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 text-sm px-3 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? "Deleting..." : "Delete"}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}